import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'

import { EMPTY_LIST } from '../../../constants/common'
import { QA_NAME_DELETE_ITEM } from '../../../constants/qa'
import { preventDefault, stopPropagation } from '../../../utils/events'
import { ActionSelect } from '../../Action'
import Button, { BUTTON_HEIGHT_24, ButtonIcon } from '../../Button'
import { Counter } from '../../Counter'
import { FloatingTooltip } from '../../Floating'
import Text from '../../Text'
import { getFilterCountEmpty, hasAvailableActions } from '../Accordion.utils'

import { getChevronIconProps, isDefaultStyle } from './AccordionButton.utils'

import * as styles from './AccordionButton.module.css'

import DeleteIcon from '../../../icons/delete.svg'

function AccordionButton({
  accordionTitle,
  actions = EMPTY_LIST,
  addButton,
  className,
  disableActiveStyle = false,
  disabled,
  getFilterCount = getFilterCountEmpty,
  hiddenArrowIcon = false,
  index,
  initOpen,
  leftIcon,
  open,
  qaName,
  titleClassName,
  titleContent, // JSX for visible title (e.g., with <span> highlights)
  variant,
  onClick,
  onClickAction,
  onDelete,
  onOpen,
}) {
  const { t } = useTranslation()

  useEffect(() => {
    if (initOpen) {
      onOpen(index)
    }
  }, [])

  const isActive = open && variant === 'style2' && !disableActiveStyle

  const buttonClassName = classNames(
    styles.button,
    {
      [styles.active]: isActive,
      [styles.style1]: variant === 'style1',
      [styles.style2]: variant === 'style2',
    },
    className
  )

  const isDefaultStyleVariant = isDefaultStyle(variant)

  const hasActions = hasAvailableActions(actions)

  const buttonIconPlacementPropsKey = isDefaultStyleVariant
    ? 'afterIconProps'
    : 'beforeIconProps'

  const buttonProps = {
    [buttonIconPlacementPropsKey]: getChevronIconProps({
      hasTooltip: isDefaultStyleVariant,
      isOpen: open,
      t,
    }),
  }

  const textProps = isDefaultStyleVariant ? { isSecondary: true } : undefined

  const count = getFilterCount()

  function handleClickDelete(e) {
    stopPropagation(e)
    preventDefault(e)

    onDelete(e)
  }

  return (
    <div className={styles.buttonRow}>
      <Button.Text
        {...(!hasActions && !hiddenArrowIcon ? buttonProps : null)}
        isGrey
        className={buttonClassName}
        count={count}
        disabled={disabled}
        isActive={isActive}
        qaName={qaName}
        onClick={onClick}
      >
        {hasActions ? (
          <div className={styles.row}>
            {leftIcon ? (
              <div className={styles.leftIconWrapper}>{leftIcon}</div>
            ) : null}
            <div className={styles.mainContent}>
              <div
                className={classNames(styles.hoverArea, {
                  [styles.disabledPointerEvents]: hiddenArrowIcon,
                })}
              >
                <Text
                  {...textProps}
                  className={classNames(styles.textWithActions, titleClassName)}
                >
                  {titleContent ?? accordionTitle}
                </Text>
                {hiddenArrowIcon ? null : (
                  <ButtonIcon
                    {...getChevronIconProps({
                      hasTooltip: true,
                      isOpen: open,
                      t,
                    })}
                  />
                )}
              </div>
              {addButton}
            </div>
          </div>
        ) : (
          <FloatingTooltip content={accordionTitle} disabled={disabled}>
            <Text
              {...textProps}
              className={classNames(styles.text, titleClassName)}
            >
              {titleContent ?? accordionTitle}
            </Text>
          </FloatingTooltip>
        )}
        {isDefaultStyleVariant ? (
          <Counter className={styles.counter} count={count} />
        ) : null}
      </Button.Text>

      {hasActions ? (
        <div className={styles.actionsButtonWrapper}>
          <ActionSelect actions={actions} onClickAction={onClickAction} />
        </div>
      ) : null}

      {onDelete && !disabled && (
        <Button.Outlined
          isGrey
          beforeIconProps={{
            Icon: DeleteIcon,
          }}
          className={styles.deleteButton}
          height={BUTTON_HEIGHT_24}
          qaName={QA_NAME_DELETE_ITEM}
          tooltip={t('Accordion.DeleteTooltipKey')}
          onClick={handleClickDelete}
        />
      )}
    </div>
  )
}

export default AccordionButton
